/**
 * Vehicle Verification API Client
 * Handles vehicle plate + PIN verification for billing mode determination
 * 
 * API Endpoint: POST /api/vehicles/verify
 * Request: { plate: string, pin: string, stationId?: string }
 * Response: { verified: boolean, vehicleId?: string, plate?: string, billingMode?: string }
 *
 * The API no longer returns a rate override (plan v3.1 §9.3 E): the
 * station / server tariff is authoritative. Repeated failures are
 * rate-limited server-side:
 *   429 { verified: false, error: 'PIN_VERIFY_RATE_LIMITED', retryAfterSeconds } + Retry-After
 */

import axios from 'axios';

/**
 * Verify vehicle plate + PIN
 * Returns the verification result with the vehicle's billing mode.
 * 
 * @param {Object} params
 * @param {string} params.plate - License plate (any format, will be normalized by backend)
 * @param {string} params.pin - 4-digit PIN
 * @param {string} [params.stationId] - The charger the driver is at
 * @returns {Promise<Object>} { verified: boolean, vehicleId?, plate?, billingMode? }
 *   or { verified: false, error: 'PIN_VERIFY_RATE_LIMITED', retryAfterSeconds }
 *   or { verified: false, error: 'SERVER_ERROR' | 'NETWORK_ERROR' }
 */
export async function verifyVehiclePin({ plate, pin, stationId }) {
  try {
    console.log('[VehiclesAPI] Verifying vehicle:', { plate: plate.substring(0, 3) + '***' });
    
    // stationId is required for a vehicle limited to selected chargers:
    // without it the API cannot tell whether THIS charger is allowed and
    // refuses (verified: false), which sent drivers who are allowed here to
    // the card flow.
    const response = await axios.post(`${import.meta.env.VITE_API_URL}/vehicles/verify`, {
      plate: plate.trim(),
      pin: pin.trim(),
      ...(stationId ? { stationId } : {}),
    });

    const data = response.data || {};
    
    console.log('[VehiclesAPI] Verification result:', {
      verified: data.verified,
      billingMode: data.billingMode,
    });

    // A rate override from an older API build is ignored: the station /
    // server tariff is authoritative.
    const { rateOverride: _ignored, ...rest } = data;
    return rest;
  } catch (error) {
    console.error('[VehiclesAPI] Verification exception:', error);
    
    // Check if it's an HTTP error response
    if (error.response?.status === 429) {
      const body = error.response.data || {};
      const header = Number.parseInt(
        error.response.headers?.['retry-after'] ?? error.response.headers?.['Retry-After'] ?? '',
        10,
      );
      const fromBody = Number(body.retryAfterSeconds);
      const retryAfterSeconds = Number.isFinite(fromBody) && fromBody > 0
        ? fromBody
        : (Number.isFinite(header) && header > 0 ? header : null);
      return {
        verified: false,
        error: 'PIN_VERIFY_RATE_LIMITED',
        retryAfterSeconds,
      };
    }

    if (error.response) {
      console.error('[VehiclesAPI] Server error:', error.response.status);
      return {
        verified: false,
        error: 'SERVER_ERROR',
        message: 'Unable to verify vehicle at this time'
      };
    }
    
    // Network or other errors
    return {
      verified: false,
      error: 'NETWORK_ERROR',
      message: 'Unable to connect to verification service'
    };
  }
}

/**
 * Normalize plate for display (frontend only)
 * Backend handles authoritative normalization
 *
 * @param {string} plate - Raw plate input
 * @returns {string} - Normalized plate (uppercase, no spaces/dashes)
 */
export function normalizePlateDisplay(plate) {
  if (!plate) return '';
  return plate
    .trim()
    .toUpperCase()
    .replace(/[\s\-_]/g, '');
}

/**
 * Neutral "have we seen this plate before?" lookup.
 *
 * Powers the returning-vehicle conversion prompt in Charge Setup: if
 * the backend recognises the plate, the wizard offers "Continue with
 * GreenCharge" as an optional sign-in / claim path alongside the
 * existing "Continue as guest" flow.
 *
 * Wire contract:
 *   POST /api/vehicles/returning-lookup
 *   Body:     { plate: string }
 *   Response: { recognised: boolean }
 *
 * The response is deliberately minimal — the endpoint NEVER returns
 * owner name, email, user id, billing mode, PIN state, or any
 * account-lifecycle state. Any error (rate limited, network down,
 * malformed body, unknown plate) resolves to `{ recognised: false }`
 * so the caller falls through to the existing guest journey silently.
 *
 * NOTE: uses plain axios (no Bearer). The endpoint is public; sending
 * an authenticated request would still work but wastes the header.
 *
 * @param {string} plate raw plate string (user input, any format)
 * @param {object} [opts]
 * @param {AbortSignal} [opts.signal] pass an AbortController.signal to
 *        cancel a stale lookup (the ChargeSetup debouncer uses this to
 *        drop responses for plates the user has since edited).
 * @returns {Promise<{ recognised: boolean }>}
 */
export async function lookupReturningVehicle(plate, { signal } = {}) {
  const trimmed = String(plate ?? '').trim();
  // Client-side guard mirrors the server's `normalizePlate` minimum
  // (>= 2 alphanumeric chars). Below that the endpoint would return
  // `recognised:false` anyway; skip the round-trip.
  if (trimmed.length < 2) {
    return { recognised: false };
  }

  try {
    const response = await axios.post(
      `${import.meta.env.VITE_API_URL}/vehicles/returning-lookup`,
      { plate: trimmed },
      { signal },
    );
    const data = response.data;
    // Coerce defensively — a broken backend that returns something
    // other than the neutral shape must never fall through to the
    // "recognised" branch.
    return { recognised: data?.recognised === true };
  } catch (error) {
    // Cancelled request — bubble the cancellation upward instead of
    // returning a fake "not recognised" that might race a fresher
    // response. Callers already handle the cancellation control flow.
    if (axios.isCancel?.(error) || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
      throw error;
    }
    // Anything else (network error, 500, rate-limit 200 body) → silent
    // fallback to the guest journey.
    return { recognised: false };
  }
}


/**
 * Registered-path plate check: "is this a registered GreenCharge vehicle?"
 *
 * Same public endpoint and neutral answer as lookupReturningVehicle (only
 * `{ recognised }` -- never owner, account or PIN details), but a failure is
 * reported, not folded into "not recognised": a driver told their
 * registered plate is unknown because the network blinked would be wrong.
 *
 *   resolves  { recognised: boolean }
 *   throws    { kind: 'unavailable' }  network error, timeout, 5xx, 429
 *
 * Nothing is authorised by this answer; plate + PIN verification still
 * decides. Callers send the normalised (uppercase) plate.
 */
export const REGISTERED_PLATE_LOOKUP_TIMEOUT_MS = 8000;

export async function checkRegisteredPlate(plate) {
  const trimmed = String(plate ?? '').trim();
  if (trimmed.length < 2) return { recognised: false };
  try {
    const response = await axios.post(
      `${import.meta.env.VITE_API_URL}/vehicles/returning-lookup`,
      { plate: trimmed },
      { timeout: REGISTERED_PLATE_LOOKUP_TIMEOUT_MS },
    );
    return { recognised: response?.data?.recognised === true };
  } catch (error) {
    const unavailable = new Error('Plate lookup unavailable');
    unavailable.kind = 'unavailable';
    unavailable.status = error?.response?.status ?? null;
    throw unavailable;
  }
}
